import { spacing, typography } from "../../app/design-system";
import { Button,Progress,Spin,Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useAuth } from "../auth/useAuth";
import { useI18n } from "../i18n/useI18n";
import { formatPercent } from "../units/format";
import { useEffectiveUnits } from "../units/useEffectiveUnits";
import { type JobInfo,useJobTracker } from "../jobs/JobTrackerContext";
import { isImportAdapterCommitJob } from "../../pages/admin/importAdapters/importAdapterJobs";
import { translateJobMessage } from "../../pages/admin/importAdapters/importAdapterDomain";
import { FullScreenWaitBackdrop } from "../../../../../../shared/frontend/components/FullScreenWaitBackdrop";

const COMMIT_REFRESH_INTERVAL_MS = 4000;

const PERCENT_MAX = 100;

function isActiveImportCommitJob(job: JobInfo): boolean {
  return isImportAdapterCommitJob(job)
    && !job.finishedAt
    && !job.error
    && (job.status === "Queued" || job.status === "Running");
}

function getImportPercent(job: JobInfo): number {
  if (job.total <= 0) return 0;
  return Math.max(0, Math.min(PERCENT_MAX, Math.round((job.current / job.total) * PERCENT_MAX)));
}

/**
 * Vollflächige Sperre, solange für den Mandanten des angemeldeten Kontos ein Import-Commit läuft.
 * Platform-Admins sind nicht betroffen (Flag im Backend immer false).
 */
export function TenantImportCommitOverlay() {
  const units = useEffectiveUnits();
  const { text } = useI18n();
  const auth = useAuth();
  const { jobs } = useJobTracker();
  const currentTenantId = (auth.info?.tenantId ?? "").trim();

  const importJob = React.useMemo(() => {
    if (!currentTenantId) return null;
    return Object.values(jobs)
      .filter((job) => isActiveImportCommitJob(job) && (job.tenantId ?? "").trim() === currentTenantId)
      .sort((a, b) => (b.startedAt ?? b.id).localeCompare(a.startedAt ?? a.id))[0] ?? null;
  }, [jobs, currentTenantId]);

  React.useEffect(() => {
    if (!auth.info?.tenantImportCommitInProgress) return;
    const id = window.setInterval(() => {
      auth.refreshMe().catch(() => {});
    }, COMMIT_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [auth.info?.tenantImportCommitInProgress, auth.refreshMe]);

  const onLogout = () => {
    auth.logout();
    // Overlay rendert außerhalb von RouterProvider — kein useNavigate (Invariant).
    const { pathname, search } = window.location;
    window.location.replace(`${pathname}${search}#/login`);
  };

  const percent = importJob ? getImportPercent(importJob) : 0;
  const hasServerProgress = !!importJob && importJob.total > 0;
  const progressMessage = importJob?.message
    ? translateJobMessage(importJob.message, text)
    : text("Tenant data import in progress hint");

  return (
    <FullScreenWaitBackdrop zIndex={10050}>
      <div
        role="dialog"
        aria-modal
        aria-labelledby="tenant-import-commit-overlay-title"
        style={{ display: "grid", gap: spacing.relaxed, textAlign: "center" }}
      >
        <Typography.Title
          id="tenant-import-commit-overlay-title"
          level={3}
          style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: 600 }}
        >
          {text("Tenant data import in progress")}
        </Typography.Title>
        <Spin size="large" />
        <Progress
          percent={percent}
          status="active"
          format={() => hasServerProgress ? formatPercent(percent, units) : text("Tenant data import progress pending")}
          style={{ width: "min(420px, 100%)", margin: "0 auto" }}
        />
        <Typography.Text type="secondary">{progressMessage}</Typography.Text>
        <div>
          <Button type="primary" onClick={onLogout}>
            {text("Log out")}
          </Button>
        </div>
      </div>
    </FullScreenWaitBackdrop>
  );
}
